import React, { useState } from 'react';
import {
  CircleCheck,
  Copy,
  Check,
  Mail,
  Send,
  TriangleAlert,
  Webhook,
} from 'lucide-react';
import { QRCode, toast } from '../../../../ui';
import { API_BASE_URL } from '../../../../configs/configs';
import {
  describeHours,
  describeWeekdays,
} from '../../../../configs/contactInfo';
import { settingsAPI } from '../../services/api';
import { getErrorMessage } from '../../../../utils/apiError';

const UPI_ID_RE = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;

const CopyValue = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select the text and copy it instead.");
    }
  };
  return (
    <div className="adm-set-copy">
      <code>{value}</code>
      <button
        type="button"
        className="adm-icon-btn"
        onClick={copy}
        aria-label={copied ? `${label} copied` : `Copy ${label}`}
      >
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </button>
    </div>
  );
};

/** What customers can pay with right now (shown on the payment sections). */
export const CheckoutStrip = ({ methods = [], onOpen }) => {
  const offered = methods.filter(m => m.offered);
  if (!offered.length) {
    return (
      <div className="pk-alert adm-set-strip" data-tone="danger" role="status">
        <TriangleAlert aria-hidden="true" />
        <span>
          <strong>Customers can't pay right now.</strong> No payment method is
          both turned on and set up.{' '}
          <button
            type="button"
            className="adm-set-link"
            onClick={() => onOpen('upi')}
          >
            Set up UPI QR
          </button>
        </span>
      </div>
    );
  }
  return (
    <div className="pk-alert adm-set-strip" data-tone="success" role="status">
      <CircleCheck aria-hidden="true" />
      <span>
        <strong>At checkout now:</strong>{' '}
        {offered.map((m, i) => (
          <React.Fragment key={m.key}>
            {i > 0 && ', '}
            {m.displayName}
            {m.isDefault && ' (preselected)'}
          </React.Fragment>
        ))}
        .
        {methods
          .filter(m => m.enabled && !m.configured)
          .map(m => (
            <span key={m.key}>
              {' '}
              {m.displayName} is on but not set up yet, so it's hidden.
            </span>
          ))}
      </span>
    </div>
  );
};

/** The QR customers scan, built from the UPI ID and payee name being edited. */
export const UpiPreview = ({ upiId, payeeName }) => {
  const valid = UPI_ID_RE.test(String(upiId || '').trim());
  const payee = String(payeeName || '').trim() || 'Projects Kart';
  // No amount: scanning this to check the payee name never pre-fills a payment.
  const uri = valid
    ? `upi://pay?pa=${upiId.trim()}&pn=${encodeURIComponent(payee)}&cu=INR`
    : null;

  return (
    <section
      className="adm-set-extra adm-set-upi"
      aria-labelledby="set-upi-preview"
    >
      <div className="adm-set-upi__qr">
        {uri ? (
          <QRCode
            value={uri}
            size={148}
            title={`UPI QR code for ${payee}, ${upiId.trim()}`}
          />
        ) : (
          <div className="adm-set-upi__empty">
            Enter a UPI ID to see the QR code
          </div>
        )}
      </div>
      <div>
        <h3 className="adm-set-extra__title" id="set-upi-preview">
          Check before you save
        </h3>
        <p className="adm-note">
          Scan this with your UPI app and check that it shows{' '}
          <strong>your</strong> name and bank. You don't need to pay. At
          checkout the QR also fills in the exact amount and the order number.
        </p>
        {uri && (
          <dl className="pk-rows adm-set-upi__rows">
            <div>
              <dt>Pay to</dt>
              <dd>{payee}</dd>
            </div>
            <div>
              <dt>UPI ID</dt>
              <dd>{upiId.trim()}</dd>
            </div>
          </dl>
        )}
      </div>
    </section>
  );
};

/** Values to copy into the PhonePe dashboard. */
export const PhonePeHelp = () => {
  const api = API_BASE_URL.replace(/\/+$/, '');
  const site = window.location.origin;
  return (
    <section className="adm-set-extra" aria-labelledby="set-phonepe-help">
      <h3 className="adm-set-extra__title" id="set-phonepe-help">
        <Webhook aria-hidden="true" />
        For the PhonePe dashboard
      </h3>
      <p className="adm-note">
        Add this webhook URL in PhonePe with the same username and password you
        enter here. It lets PhonePe tell the store about payments even when
        customers close the page.
      </p>
      <CopyValue
        value={`${api}/api/payments/phonepe/webhook`}
        label="webhook URL"
      />
      <p className="adm-note adm-set-extra__gap">
        This site's return page, for the Return page setting:
      </p>
      <CopyValue value={`${site}/payment-status`} label="return page URL" />
    </section>
  );
};

/** Sends a test email with the saved SMTP settings. */
export const TestEmail = ({ disabled }) => {
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const response = await settingsAPI.sendTestEmail(to.trim());
      setResult({
        ok: true,
        message: response.data?.message || 'Test email sent.',
      });
    } catch (error) {
      setResult({
        ok: false,
        message: getErrorMessage(error, "The test email wasn't sent."),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="adm-set-extra" aria-labelledby="set-email-test">
      <h3 className="adm-set-extra__title" id="set-email-test">
        <Mail aria-hidden="true" />
        Send a test email
      </h3>
      <p className="adm-note">
        {disabled
          ? 'Save your changes first: the test uses the saved settings.'
          : 'Uses the saved settings. Leave the address empty to send it to yourself.'}
      </p>
      <div className="adm-set-test">
        <label htmlFor="set-email-test-to" className="sr-only">
          Send the test to
        </label>
        <input
          id="set-email-test-to"
          className="pk-input"
          type="email"
          inputMode="email"
          placeholder="you@example.com"
          value={to}
          onChange={e => setTo(e.target.value)}
          disabled={disabled || sending}
        />
        <button
          type="button"
          className="pk-btn pk-btn--secondary pk-btn--sm"
          onClick={send}
          disabled={disabled || sending}
          aria-busy={sending || undefined}
        >
          <Send aria-hidden="true" />
          {sending ? 'Sending…' : 'Send test'}
        </button>
      </div>
      {result && (
        <div
          className="pk-alert adm-set-extra__gap"
          data-tone={result.ok ? 'success' : 'danger'}
          role="status"
        >
          {result.ok ? (
            <CircleCheck aria-hidden="true" />
          ) : (
            <TriangleAlert aria-hidden="true" />
          )}
          <span>{result.message}</span>
        </div>
      )}
    </section>
  );
};

/** How the support hours read on the site. */
export const HoursPreview = ({ days, openHour, closeHour }) => {
  const list = Array.isArray(days) ? days.map(Number) : [];
  const open = Number(openHour);
  const close = Number(closeHour);
  const valid = list.length > 0 && close > open;
  return (
    <section className="adm-set-extra" aria-live="polite">
      <h3 className="adm-set-extra__title">Shown on the site as</h3>
      <p className="adm-set-preview-line">
        {valid
          ? `${describeWeekdays(list)}, ${describeHours(open, close)}`
          : 'Pick at least one day, and a closing time after the opening time.'}
      </p>
    </section>
  );
};

/** Roughly how the homepage looks in Google results. */
export const SearchPreview = ({ title, description, siteUrl }) => {
  let host = '';
  try {
    host = new URL(siteUrl).host.replace(/^www\./, '');
  } catch {
    host = 'your-site.in';
  }
  return (
    <section className="adm-set-extra" aria-labelledby="set-search-preview">
      <h3 className="adm-set-extra__title" id="set-search-preview">
        Search result preview
      </h3>
      <div className="adm-set-serp">
        <span className="adm-set-serp__url">{host}</span>
        <span className="adm-set-serp__title">{title || 'Homepage title'}</span>
        <span className="adm-set-serp__desc">
          {description || 'Homepage description'}
        </span>
      </div>
      <p className="adm-note adm-set-extra__gap">
        Search engines update their copy when they next visit the site, which
        can take days.
      </p>
    </section>
  );
};
